import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/common/Button'
import { UnmatchedSubjectDialog } from '@/components/timetable-import/UnmatchedSubjectDialog'
import type { RebindCategory, SubjectRebindPreview, SubjectRebindSibling, SubjectRebindSubject } from '@/services/api/timetable'

const DAY_SHORT: Record<number, string> = {
  0: 'Mo', 1: 'Di', 2: 'Mi', 3: 'Do', 4: 'Fr', 5: 'Sa', 6: 'So',
}

const CATEGORY_LABEL: Record<RebindCategory, string> = {
  subject: 'Fach',
  room: 'Raum',
  klass: 'Klasse',
}

const CATEGORIES: RebindCategory[] = ['subject', 'room', 'klass']

export interface SubjectRebindSaveOpts {
  subjectId: string
  targetId: string
  sourceCategory: RebindCategory
  targetCategory: RebindCategory
  includeSiblings: boolean
  createAlias: boolean
  createTarget?: boolean
  createName?: string
}

interface SubjectRebindPanelProps {
  preview: SubjectRebindPreview
  anchor: DOMRect
  initialSource?: RebindCategory
  saving?: boolean
  error?: string | null
  onSave: (opts: SubjectRebindSaveOpts) => void
  onClose: () => void
}

function siblingLabel(siblings: SubjectRebindSibling[]): string | null {
  if (siblings.length === 0) return null
  const parts = siblings.map(s => {
    const day = s.day_of_week != null ? DAY_SHORT[s.day_of_week] : ''
    const time = s.time_label || ''
    const kl = s.class_name || ''
    return [day, time, kl].filter(Boolean).join(' ')
  })
  if (parts.length === 1) return `auch ${parts[0]}`
  return `auch ${siblings.length} weitere mit gleichem Token`
}

function fieldToken(preview: SubjectRebindPreview, cat: RebindCategory): string | null {
  return preview.fields?.[cat]?.token || (cat === 'subject' ? preview.token : null)
}

function defaultSource(preview: SubjectRebindPreview, initial?: RebindCategory): RebindCategory {
  if (initial) return initial
  for (const cat of CATEGORIES) {
    if (fieldToken(preview, cat)) return cat
  }
  return 'subject'
}

export function SubjectRebindPanel({
  preview,
  anchor,
  initialSource,
  saving,
  error,
  onSave,
  onClose,
}: SubjectRebindPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [sourceCategory, setSourceCategory] = useState<RebindCategory>(() => defaultSource(preview, initialSource))
  const [targetCategory, setTargetCategory] = useState<RebindCategory>(() => defaultSource(preview, initialSource))
  const sourceToken = fieldToken(preview, sourceCategory)
  const currentId = targetCategory === sourceCategory
    ? (preview.fields?.[targetCategory]?.id || (targetCategory === 'subject' ? preview.subject_id : null))
    : null
  const [picked, setPicked] = useState<string | null>(currentId)
  const canRecategorize = Boolean(preview.allow_recategorize)
  const siblings = (preview.field_siblings?.[sourceCategory] ?? (sourceCategory === 'subject' ? preview.siblings : []))
  const [includeSiblings, setIncludeSiblings] = useState(siblings.length > 0)
  const [createAlias, setCreateAlias] = useState(false)
  const [newSubjectToken, setNewSubjectToken] = useState<string | null>(null)
  const sib = siblingLabel(siblings)

  useEffect(() => {
    setSourceCategory(defaultSource(preview, initialSource))
    setTargetCategory(defaultSource(preview, initialSource))
  }, [preview, initialSource])

  useEffect(() => {
    const nextSiblings = preview.field_siblings?.[sourceCategory] ?? (sourceCategory === 'subject' ? preview.siblings : [])
    setIncludeSiblings(nextSiblings.length > 0)
    setQuery('')
    setCreateAlias(false)
    setNewSubjectToken(null)
    const same = targetCategory === sourceCategory
    const id = same
      ? (preview.fields?.[targetCategory]?.id || (targetCategory === 'subject' ? preview.subject_id : null))
      : null
    const list = targetCategory === 'room'
      ? preview.rooms ?? []
      : targetCategory === 'klass'
        ? preview.klasses ?? []
        : preview.subjects
    setPicked(id && list.some(s => s.id === id) ? id : null)
  }, [sourceCategory, targetCategory, preview])

  const catalog: SubjectRebindSubject[] = useMemo(() => {
    if (targetCategory === 'room') return preview.rooms ?? []
    if (targetCategory === 'klass') return preview.klasses ?? []
    return preview.subjects
  }, [preview.klasses, preview.rooms, preview.subjects, targetCategory])

  const items = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? catalog.filter(s => [s.name, s.code].filter(Boolean).join(' ').toLowerCase().includes(q))
      : catalog
    return [...filtered].sort(
      (a, b) => Number(b.prefix_match) - Number(a.prefix_match) || a.name.localeCompare(b.name, 'de'),
    )
  }, [catalog, query])

  const pos = useMemo(() => {
    const w = 380
    const gap = 8
    const margin = 8
    let left = anchor.right + gap
    if (left + w > window.innerWidth - margin) left = anchor.left - w - gap
    if (left < margin) left = margin
    const height = window.innerHeight - margin * 2
    return { left, top: margin, width: w, height }
  }, [anchor])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !newSubjectToken) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, newSubjectToken])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (newSubjectToken) return
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onClose, newSubjectToken])

  const day = preview.day_of_week != null ? DAY_SHORT[preview.day_of_week] : ''
  const meta = [day, preview.time_label, preview.class_name].filter(Boolean).join(' · ')
  const assigningEmpty = !sourceToken && sourceCategory === targetCategory
  const title = assigningEmpty
    ? `${CATEGORY_LABEL[sourceCategory]} hinzufügen`
    : sourceCategory === targetCategory
      ? `${CATEGORY_LABEL[sourceCategory]} umhängen`
      : `${CATEGORY_LABEL[sourceCategory]} → ${CATEGORY_LABEL[targetCategory]}`
  const searchPlaceholder = `${CATEGORY_LABEL[targetCategory]} suchen…`
  const emptyLabel = `Kein ${CATEGORY_LABEL[targetCategory]}`
  const showAlias = Boolean(preview.allow_alias && targetCategory === 'subject' && sourceToken)
  const sourceFields = canRecategorize
    ? CATEGORIES
    : CATEGORIES.filter(c => c === sourceCategory || fieldToken(preview, c))
  const targetToken = preview.fields?.[targetCategory]?.token || (targetCategory === 'subject' ? preview.token : null)
  const overwrite = Boolean(
    sourceCategory !== targetCategory
    && targetToken
    && sourceToken
    && targetToken.trim().toLowerCase() !== sourceToken.trim().toLowerCase(),
  )
  const createName = (query.trim() || sourceToken || '').trim()
  const exactMatch = Boolean(createName && catalog.some(s => s.name.toLowerCase() === createName.toLowerCase()))
  const showCreate = Boolean(preview.allow_alias && createName && !exactMatch)
  const createLabel = targetCategory === 'subject'
    ? `«${createName}» als neues Fach anlegen`
    : targetCategory === 'room'
      ? `«${createName}» als neuen Raum anlegen`
      : `«${createName}» als neue Klasse anlegen`

  return createPortal(
    <>
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby="subject-rebind-title"
      className="fixed z-[70] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
      style={{ left: pos.left, top: pos.top, width: pos.width, height: pos.height }}
    >
      <h3 id="subject-rebind-title" className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mb-2 text-xs text-gray-500">
        {meta}
        {sourceToken ? <> · Token <code className="rounded bg-gray-100 px-1">{sourceToken}</code></> : null}
      </p>
      {canRecategorize ? (
        <div className="mb-2 space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Von</p>
          <div className="flex gap-1">
            {sourceFields.map(cat => (
              <button
                key={cat}
                type="button"
                aria-label={`Von ${CATEGORY_LABEL[cat]}`}
                onClick={() => {
                  setSourceCategory(cat)
                  if (!fieldToken(preview, cat)) setTargetCategory(cat)
                }}
                className={`rounded border px-2 py-1 text-xs ${
                  sourceCategory === cat
                    ? 'border-blue-600 bg-blue-50 text-blue-800'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {CATEGORY_LABEL[cat]}
              </button>
            ))}
          </div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Als</p>
          <div className="flex gap-1">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                aria-label={`Als ${CATEGORY_LABEL[cat]}`}
                onClick={() => setTargetCategory(cat)}
                className={`rounded border px-2 py-1 text-xs ${
                  targetCategory === cat
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {CATEGORY_LABEL[cat]}
              </button>
            ))}
          </div>
          {overwrite ? (
            <p className="text-xs text-amber-800">
              {CATEGORY_LABEL[targetCategory]} «{targetToken}» wird überschrieben.
            </p>
          ) : null}
        </div>
      ) : null}
      <input
        type="search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
      <div className="mb-2 min-h-0 flex-1 overflow-y-auto">
        {items.map(s => {
          const sel = picked === s.id
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setPicked(s.id)}
              className={`mb-1 w-full rounded-md border px-2 py-1.5 text-left text-sm ${
                sel ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              {s.name}
              {s.prefix_match ? (
                <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] font-semibold uppercase text-emerald-700">Präfix</span>
              ) : null}
            </button>
          )
        })}
        {items.length === 0 ? (
          <p className="px-1 py-2 text-xs text-gray-500">{emptyLabel}</p>
        ) : null}
      </div>
      {sib ? (
        <label className="mb-2 flex items-start gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeSiblings}
            onChange={e => setIncludeSiblings(e.target.checked)}
          />
          {sib}
        </label>
      ) : null}
      {showAlias ? (
        <label className="mb-2 flex items-start gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={createAlias}
            onChange={e => setCreateAlias(e.target.checked)}
          />
          Als Schul-Alias speichern ({sourceToken} → gewähltes Fach)
        </label>
      ) : null}
      {createAlias && showAlias ? (
        <p className="mb-2 text-xs text-amber-800">Gilt schulweit. Andere Personen mit diesem Token erben das Fach beim nächsten Import.</p>
      ) : null}
      {error ? <p className="mb-2 text-xs text-red-600">{error}</p> : null}
      {showCreate ? (
        <button
          type="button"
          disabled={saving}
          className="mb-2 w-full rounded-md border border-dashed border-blue-300 px-2 py-1.5 text-left text-xs text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          onClick={() => {
            if (targetCategory === 'subject') {
              setNewSubjectToken(createName)
              return
            }
            onSave({
              subjectId: '',
              targetId: '',
              sourceCategory,
              targetCategory,
              includeSiblings: Boolean(sib) && includeSiblings,
              createAlias: false,
              createTarget: true,
              createName,
            })
          }}
        >
          {createLabel}
        </button>
      ) : null}
      <div className="flex shrink-0 justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button
          type="button"
          size="sm"
          disabled={!picked || saving || (sourceCategory !== targetCategory && !sourceToken)}
          isLoading={saving}
          onClick={() => {
            if (!picked) return
            onSave({
              subjectId: picked,
              targetId: picked,
              sourceCategory,
              targetCategory,
              includeSiblings: Boolean(sib) && includeSiblings,
              createAlias: showAlias && createAlias,
            })
          }}
        >
          Speichern
        </Button>
      </div>
    </div>
    {newSubjectToken ? (
      <UnmatchedSubjectDialog
        open
        token={newSubjectToken}
        hasQualificationMatrix
        onResolve={(_type, data) => {
          const id = String(data.canonical_subject_id || data.subject_id || '')
          setNewSubjectToken(null)
          if (!id) return
          onSave({
            subjectId: id,
            targetId: id,
            sourceCategory,
            targetCategory: 'subject',
            includeSiblings: Boolean(sib) && includeSiblings,
            createAlias: false,
          })
        }}
        onClose={() => setNewSubjectToken(null)}
      />
    ) : null}
    </>,
    document.body,
  )
}
